import { useMemo, useState, useCallback } from 'react';
import {
  Text, Paper, Stack, Group, Table, Skeleton, Drawer,
  SimpleGrid, Divider, ActionIcon, Tooltip, Pagination,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconChevronUp, IconChevronDown, IconSelector, IconEye, IconReceipt,
} from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import StatusBadge from '@/components/StatusBadge';
import EmptyState from '@/components/EmptyState';
import { usePettyCashBoxes } from '@/hooks/usePortalApi';

type SortField = 'box_name';
type SortDir = 'asc' | 'desc';
const PAGE_SIZE = 20;

function SortIcon({ field, current, dir }: { field: SortField; current: SortField | null; dir: SortDir }) {
  if (current !== field) return <IconSelector size={14} style={{ opacity: 0.4 }} />;
  return dir === 'asc' ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />;
}

function formatCurrency(val: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
}

export default function PettyCash() {
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [drawerItem, setDrawerItem] = useState<any>(null);
  const [drawerOpen, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);

  const { data, isLoading } = usePettyCashBoxes();
  const allItems = data?.items ?? [];

  const sorted = useMemo(() => {
    if (!sortField) return allItems;
    return [...allItems].sort((a, b) => {
      const cmp = String(a[sortField] ?? '').localeCompare(String(b[sortField] ?? ''));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [allItems, sortField, sortDir]);

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const items = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
    setPage(1);
  };

  const handleView = useCallback((item: any) => { setDrawerItem(item); openDrawer(); }, [openDrawer]);

  const ThSort = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <Table.Th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort(field)}>
      <Group gap={4} wrap="nowrap">{children}<SortIcon field={field} current={sortField} dir={sortDir} /></Group>
    </Table.Th>
  );

  return (
    <Stack gap="lg">
      <PageHeader title="Petty Cash" description="Manage petty cash boxes and balances" />
      {isLoading ? (
        <Stack gap="xs">{[...Array(5)].map((_, i) => <Skeleton key={i} height={50} radius="md" />)}</Stack>
      ) : items.length === 0 ? (
        <EmptyState icon={<IconReceipt size={30} />} title="No petty cash boxes" description="No petty cash boxes found" />
      ) : (
        <>
          <Paper>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={60}>ID</Table.Th>
                  <ThSort field="box_name">Box Name</ThSort>
                  <Table.Th>Custodian</Table.Th>
                  <Table.Th>Balance</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th w={80} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((item: any) => (
                  <Table.Tr key={item.id}>
                    <Table.Td><Text size="sm" style={{ fontFamily: 'monospace' }}>{item.id}</Text></Table.Td>
                    <Table.Td><Text size="sm" fw={500}>{item.box_name}</Text></Table.Td>
                    <Table.Td><Text size="sm">{item.custodian}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm" fw={500}>{formatCurrency(item.balance ?? 0)}</Text></Table.Td>
                    <Table.Td><StatusBadge status={item.status ?? 'active'} /></Table.Td>
                    <Table.Td>
                      <Tooltip label="View"><ActionIcon variant="subtle" size="sm" onClick={() => handleView(item)}><IconEye size={16} /></ActionIcon></Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
          {totalPages > 1 && <Group justify="center"><Pagination total={totalPages} value={page} onChange={setPage} /></Group>}
        </>
      )}
      <Drawer opened={drawerOpen} onClose={closeDrawer} position="right" size="lg" title={<Text fw={700} size="lg">Petty Cash Details</Text>}>
        {drawerItem && (
          <Stack gap="md">
            <StatusBadge status={drawerItem.status ?? 'active'} />
            <Divider />
            <SimpleGrid cols={2}>
              <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>ID</Text><Text size="sm" fw={500}>{drawerItem.id}</Text></div>
              <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Box Name</Text><Text size="sm" fw={500}>{drawerItem.box_name}</Text></div>
              <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Custodian</Text><Text size="sm" fw={500}>{drawerItem.custodian}</Text></div>
              <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Balance</Text><Text size="sm" fw={500}>{formatCurrency(drawerItem.balance ?? 0)}</Text></div>
            </SimpleGrid>
          </Stack>
        )}
      </Drawer>
    </Stack>
  );
}
